import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, BatteryCharging, Wifi, Droplets, ShieldCheck, Home as HomeIcon, MonitorSmartphone, Activity, Droplet, Waves, Thermometer, Layers, Check } from "lucide-react";
import Logo from "../components/Logo";
import Reveal from "../components/Reveal";
import { PROBES, STRIP_A, STRIP_B, STRIP_C, WORKFLOW } from "../data/build";

const MOBILE_IMG = "https://static.prod-images.emergentagent.com/jobs/a99c2969-7ac0-4b5c-adb1-9cb1c6716fac/images/2ea8dea3d6180e731c9b5f3620368adbe7b4bd903e1fbb2110c9df2b48e9821a.jpeg";

const NAV = [
  { to: "/", label: "Hub" },
  { to: "/shop", label: "Pool Shop" },
  { to: "/software", label: "Software" },
];

const SPECS = [
  { k: "Format", v: "Portable field unit · rugged carry case" },
  { k: "Core", v: "Same 4 probes + 3 strip slots as the shop tester" },
  { k: "Rinse", v: "1.5 L refillable rinse bottle (internal)" },
  { k: "Waste", v: "1.5 L sealed waste bottle (internal)" },
  { k: "Power", v: "14.8 V Li-ion · ~6 h field runtime" },
  { k: "Panel", v: "Full panel in ~60–90 s" },
  { k: "Sync", v: "Pushes results to the shop over WiFi / mobile" },
  { k: "Reagents", v: "None — no liquids or cartridges" },
];

const FIELD = [
  { icon: ShieldCheck, t: "Rugged & sealed", d: "A tough, weatherproof case with a protective bumper and fold-out handle — built for the back of a service van and poolside use." },
  { icon: Droplets, t: "Fully self-contained", d: "Top up the 1.5 L rinse bottle, empty the 1.5 L waste bottle at the end of the round — no plumbing, no drain needed on site." },
  { icon: BatteryCharging, t: "All-day battery", d: "A 14.8 V Li-ion pack gives roughly six hours of field testing between charges." },
  { icon: Wifi, t: "Syncs back to the shop", d: "Every on-site test lands in the same client database over WiFi or mobile, so the shop sees the result the moment the tech finishes." },
];

const PROBE_ICON = { P1: Activity, P2: Droplet, P3: Waves, P4: Thermometer };
const STRIPS = [STRIP_A, STRIP_B, STRIP_C];

export default function ShopMobile() {
  return (
    <div className="min-h-screen bg-[#FAFCFD]" data-testid="mobile-page">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur border-b border-slate-200 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="px-3 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors">{n.label}</Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-14 pb-16 md:pt-20 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-violet-50/70 to-white" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-mono2 text-violet-700 mb-5">
              <Truck className="h-3.5 w-3.5" /> Project 2 · Portable field tester
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Aqualux Nexus <span className="aqua-text">Mobile</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-xl">
              The shop tester's exact measurement core — four continuous probes and three top-loaded strip slots —
              packaged into a rugged, battery-powered case for on-site pool technicians. Every test syncs straight
              back to the shop's client database.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/software" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="mobile-to-software-btn">
                <MonitorSmartphone className="h-4 w-4" /> See the Software it syncs to
              </Link>
              <Link to="/shop" className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-sky-300 text-slate-800 px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="mobile-to-shop-btn">
                The in-store tester <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="rounded-3xl overflow-hidden border border-slate-200 shadow-[0_40px_80px_-40px_rgba(76,29,149,0.35)] bg-white">
            <img src={MOBILE_IMG} alt="Aqualux Nexus Mobile — portable field tester" className="w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Specs */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3">At a glance</p><h2 className="font-display text-3xl font-bold text-slate-900">Specifications</h2></Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SPECS.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.04}>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`mobile-spec-${i}`}>
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mb-1">{s.k}</p>
                  <p className="font-semibold text-slate-800">{s.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Built for the field */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3">Built for the field</p><h2 className="font-display text-3xl font-bold text-slate-900">Everything the bench unit does — on site</h2></Reveal>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {FIELD.map(({ icon: Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full" data-testid={`mobile-field-${i}`}>
                  <span className="h-10 w-10 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center mb-3"><Icon className="h-5 w-5 text-violet-600" /></span>
                  <p className="font-display font-bold text-slate-900">{t}</p>
                  <p className="text-sm text-slate-500 mt-1.5">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Shared core */}
      <section className="py-16 md:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3">Same measurement core</p><h2 className="font-display text-3xl font-bold text-slate-900">Four live probes, three smart strips</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">Identical to the shop tester — so a technician's field reading is directly comparable to the in-store result for the same pool.</p>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PROBES.map((p, i) => {
              const Icon = PROBE_ICON[p.p] || Activity;
              return (
                <Reveal key={p.p} delay={i * 0.04}>
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`mobile-probe-${p.p}`}>
                    <span className="h-10 w-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${p.color}1A`, border: `1px solid ${p.color}55` }}>
                      <Icon className="h-5 w-5" style={{ color: p.color }} />
                    </span>
                    <p className="font-display font-bold text-slate-900">{p.name}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{p.method}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-3">
            {STRIPS.map((s, i) => (
              <Reveal key={s.port} delay={i * 0.05}>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`mobile-strip-${s.port}`}>
                  <span className="inline-flex items-center gap-2 font-display font-bold text-slate-900 mb-2"><Layers className="h-4 w-4 text-violet-600" /> Slot {s.port}</span>
                  <p className="font-semibold text-slate-800">{s.label}</p>
                  <ul className="mt-2 space-y-1">
                    {s.reads.map((r) => <li key={r.t} className="text-sm text-slate-500 flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" />{r.t}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><p className="eyebrow mb-3">On-site workflow</p><h2 className="font-display text-3xl font-bold text-slate-900">Test at the pool, synced to the shop</h2></Reveal>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {WORKFLOW.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6" data-testid={`mobile-step-${i}`}>
                  <span className="font-mono2 text-sm text-violet-600 font-bold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-display font-bold text-slate-900 mt-1">{s.t}</p>
                  <p className="text-sm text-slate-500 mt-1.5">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0A0F1D] text-white">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl font-bold">One ecosystem — shop, mobile and home</h2>
            <p className="mt-3 text-slate-400 max-w-2xl mx-auto">The Mobile unit shares the shop tester's core and feeds the same central Software. Next in the family is the installed home automation system.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/software" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white px-7 py-3.5 rounded-full font-semibold transition-colors" data-testid="mobile-cta-software-btn"><MonitorSmartphone className="h-4 w-4" /> Open the Software</Link>
              <Link to="/home" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/15 text-white px-7 py-3.5 rounded-full font-semibold transition-colors" data-testid="mobile-cta-home-btn"><HomeIcon className="h-4 w-4" /> Home automation</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
